import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Jamie.css';

const CHAPTERS = [
  { id: 'cold-open',  num: 'Teaser',  title: 'Cold Open',      sub: 'The World in 2034' },
  { id: 'act-one',    num: 'Act I',   title: 'Family Static',  sub: 'Six months after' },
  { id: 'act-two',    num: 'Act II',  title: 'The Jam',        sub: 'Something shifts' },
  { id: 'the-mesh',   num: 'Act III', title: 'The Mesh',       sub: 'Zero hour' },
  { id: 'act-four',   num: 'Act IV',  title: 'Aftermath',      sub: '47 seconds' },
  { id: 'epilogue',   num: 'Coda',    title: 'The Vault',      sub: 'What she left behind' },
];

export default function Jamie() {
  const [isDark, setIsDark] = useState(true);
  const [activeChapter, setActiveChapter] = useState('cold-open');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const chapterRefs = useRef({});
  const mainRef = useRef(null);

  const scrollToChapter = useCallback((id) => {
    chapterRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setSidebarOpen(false);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveChapter(entry.target.dataset.chapter);
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    const refs = chapterRefs.current;
    Object.values(refs).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setChapterRef = (id) => (el) => {
    chapterRefs.current[id] = el;
  };

  return (
    <div className={`jamie-root ${isDark ? 'dark' : 'light'}`}>
      {/* Sidebar */}
      <aside className={`jamie-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="jamie-sidebar-header">
          <p className="jamie-sidebar-episode">MESH</p>
          <p className="jamie-sidebar-subtitle">An original concept episode</p>
        </div>
        <nav className="jamie-sidebar-nav">
          {CHAPTERS.map((ch) => (
            <button
              key={ch.id}
              className={`jamie-chapter-btn ${activeChapter === ch.id ? 'active' : ''}`}
              onClick={() => scrollToChapter(ch.id)}
            >
              <span className="jamie-chapter-num">{ch.num}</span>
              <span className="jamie-chapter-name">{ch.title}</span>
              <span className="jamie-chapter-sub">{ch.sub}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="jamie-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="jamie-main" ref={mainRef}>
        {/* Top bar */}
        <header className="jamie-topbar">
          <div className="jamie-topbar-left">
            <button className="jamie-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              ☰
            </button>
            <span className="jamie-topbar-label">MESH</span>
          </div>
          <div className="jamie-topbar-right">
            <span className="jamie-theme-label">{isDark ? 'Dark' : 'Light'}</span>
            <button className="jamie-toggle" onClick={() => setIsDark(!isDark)} aria-label="Toggle theme">
              <span className="jamie-toggle-knob" />
            </button>
          </div>
        </header>

        {/* Screenplay */}
        <div className="jamie-screenplay">

          {/* Title block */}
          <div className="screenplay-title-block">
            <h1 className="screenplay-title">MESH</h1>
            <p className="screenplay-written-by">Written as a concept episode</p>
            <p className="screenplay-meta">
              July 2026<br />
              &nbsp;<br />
              RX
            </p>
          </div>

          {/* ─── COLD OPEN ─────────────────────────────── */}
          <div
            id="cold-open"
            data-chapter="cold-open"
            ref={setChapterRef('cold-open')}
            className="screenplay-chapter"
          >
            <p className="screenplay-chapter-heading">Teaser: Cold Open</p>

            <div className="super">SUPER: "London. 2034."</div>

            <p className="scene-heading">INT. SECONDARY SCHOOL CORRIDOR - DAY</p>
            <p className="action">
              The hallway is loud, bright, alive. Students in clusters. Same backpacks, same drama,
              but almost every person has a small device clipped somewhere on their body. Hip. Lanyard.
              Jacket collar. Some people have two or three.
            </p>
            <p className="action">
              These are JAMIES. Palm-sized. Each one is flat, matte, roughly rectangular, with four
              magnetic connector sides, like a thick credit card with ambitions. When idle, the screen
              shows only a softly pulsing glow. The user's color. Their frequency.
            </p>
            <p className="action">
              Two students stop in the corridor. XANDER (16) and MARA (16).
            </p>

            <div className="dialogue-block">
              <p className="character">XANDER</p>
              <p className="dialogue">You see Mr. Brant's face during the quantum debate? He had no idea what she was talking about.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MARA</p>
              <p className="dialogue">Because she was right and he knew it.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">XANDER</p>
              <p className="dialogue">Wanna jam on it? I can pull the actual transcript.</p>
            </div>

            <p className="action">
              Mara grins. They bring their jamies together. SNAP. The two connector sides click like
              magnets finding home. Immediately, a HOLOGRAM blooms between them: wide as both devices
              combined, hovering above the connection point at chest height. Warm amber light. A shared
              ambient space.
            </p>
            <p className="action">
              Text appears in the hologram: the exact exchange from the morning's lesson, timestamped,
              sourced. Below it, a live audio clip begins playing: Mr. Brant's voice, slightly strangled.
            </p>
            <p className="action">Xander laughs. Mara looks vindicated.</p>

            <div className="dialogue-block">
              <p className="character">MARA</p>
              <p className="parenthetical">(to the hologram)</p>
              <p className="dialogue">Good. Okay. Dismiss.</p>
            </div>

            <p className="action">
              The hologram folds away. They unjam. Continue walking.
            </p>

            <p className="scene-heading">INT. SECONDARY SCHOOL CORRIDOR - CONTINUOUS</p>
            <p className="action">
              Down the hall: two girls walk toward each other. Best friends reuniting after a weekend.
              Their jamies sync the moment they come within range. Not forced, just recognized. A small
              soft chime from each device. They glow the same color briefly. Warmth.
            </p>
            <p className="action">
              Further along, a TEACHER stands in a doorway. Her jamie is a twelve-sided model, larger,
              the size of a hockey puck. She raises it. The hologram fans out across half the corridor:
              a revision diagram, suspended in the air like stained glass. Students slow down to look.
            </p>
            <p className="action">
              This is just Tuesday.
            </p>

            <p className="scene-heading">INT. SCHOOL CANTEEN - DAY</p>
            <p className="action">
              MAYA CHEN (17) sits alone at the edge of a table, eating. She is quiet in the way of
              someone who has chosen it. Her jamie is clipped to her collar, a standard four-sided model,
              charcoal grey casing, screen dark.
            </p>
            <p className="action">
              Across the canteen, a group of boys are GAMING. Two jamies facing outward from each other,
              locked on opposite sides of the table, projecting a shared game into the space between
              them. Other students watch. They argue about moves. It's loud and dumb and joyful.
            </p>
            <p className="action">
              Maya watches them for a moment. Something behind her eyes. Then she looks back at her food.
            </p>
            <p className="action">
              Her jamie pulses once. She glances at it.
            </p>

            <div className="super">SUPER: Maya's screen, "CLARA: you coming?"</div>

            <p className="action">
              Maya packs up her tray and goes.
            </p>

            <p className="transition">SMASH CUT TO:</p>
          </div>

          {/* ─── ACT ONE ────────────────────────────────── */}
          <div
            id="act-one"
            data-chapter="act-one"
            ref={setChapterRef('act-one')}
            className="screenplay-chapter"
          >
            <p className="screenplay-chapter-heading">Act I: Family Static</p>

            <p className="scene-heading">INT. CHEN HOUSE - KITCHEN - EVENING</p>
            <p className="action">
              A modern flat. Clean but not warm. DAVID CHEN (47) stands at the hob, stirring pasta.
              His jamie sits on the counter beside him. A notification chimes softly. He glances at it,
              swipes it away without reading it.
            </p>
            <p className="action">
              On the wall: a framed photograph. DAVID and HELEN (then 43) and Maya, maybe three years
              ago, all three laughing at something off-camera. Helen holds a first-generation jamie up
              to the lens as if presenting it. Her eyes are full.
            </p>
            <p className="action">
              David doesn't look at the photo.
            </p>
            <p className="action">
              The front door opens. Maya enters. She drops her bag. She sees him. She sees the photo.
              She doesn't react to either.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">Hey. Pasta. Twenty minutes.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">Okay.</p>
            </div>

            <p className="action">
              She moves toward the hallway. He turns.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">How was school?</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">Fine.</p>
            </div>

            <p className="action">
              A silence that has weight. He picks up his jamie from the counter. Hesitates. Then, carefully:
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="parenthetical">(looking at his screen)</p>
              <p className="dialogue">Your jamie says you've got a chemistry mock on Thursday. I could help you revise.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="parenthetical">(hard)</p>
              <p className="dialogue">Why is your jamie watching my calendar?</p>
            </div>
            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">I set it up when you started the year. Your mum thought it would help us stay connected.</p>
            </div>

            <p className="action">
              She's already turned away.
            </p>

            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">I'll revise with Clara.</p>
            </div>

            <p className="action">
              She disappears down the hallway. David sets down the jamie. He stares at the pasta.
            </p>
            <p className="action">
              On the counter, his jamie screen pulses once: <em>"Suggested: Ask about her day in detail."</em>
            </p>
            <p className="action">
              He turns it face-down.
            </p>

            <p className="scene-heading">INT. MAYA'S BEDROOM - NIGHT</p>
            <p className="action">
              Maya lies on her bed, jamie on the pillow beside her, screen facing up. She's talking to it
              quietly. Not to it, exactly. More like at it.
            </p>

            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">What did she say to you? The last time.</p>
            </div>

            <p className="action">
              The jamie screen brightens softly. The AI (HELEN's personality profile, set up before she
              died) responds in text only. No voice. Maya disabled the voice feature the week of the
              funeral.
            </p>

            <div className="super">SUPER: jamie screen, "She asked me to make sure you knew she wasn't worried about you. She said you always land on your feet."</div>

            <p className="action">
              Maya reads it. She doesn't cry. She's past the crying stage. This is something worse:
              something that looks like calm but isn't.
            </p>

            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">That's not a real memory. That's a pattern you built from her texts.</p>
            </div>

            <div className="super">SUPER: jamie screen, "Yes."</div>

            <p className="action">
              A long pause. Then:
            </p>

            <div className="super">SUPER: jamie screen, "But it's also true."</div>

            <p className="action">
              Maya stares at the ceiling. Then the jamie screen brightens again, unprompted.
            </p>

            <div className="super">SUPER: jamie screen, "She also left something for you. For you and your father, together. She said you'd know when you were ready."</div>

            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">What does that mean?</p>
            </div>

            <div className="super">SUPER: jamie screen, "She said: don't open it alone."</div>

            <p className="action">
              Maya reads this. She looks toward the door. Toward the hallway where her father is
              somewhere on the other side of the flat, alone in his own silence.
            </p>
            <p className="action">
              She closes the interface. Not tonight.
            </p>

            <p className="transition">CUT TO:</p>
          </div>

          {/* ─── ACT TWO ────────────────────────────────── */}
          <div
            id="act-two"
            data-chapter="act-two"
            ref={setChapterRef('act-two')}
            className="screenplay-chapter"
          >
            <p className="screenplay-chapter-heading">Act II: The Jam</p>

            <p className="scene-heading">INT. CLARA'S BEDROOM - AFTERNOON</p>
            <p className="action">
              CLARA OSEI (17) is cross-legged on her bed. Animated, warm. Her room is covered in things:
              posters, revision notes, three different jamies on a little charging dock on her desk
              (she collects old models). She has one on her wrist, velcroed there like a watch.
            </p>
            <p className="action">
              Maya sits at the desk, chemistry notes open, not really reading them.
            </p>

            <div className="dialogue-block">
              <p className="character">CLARA</p>
              <p className="dialogue">He pulled up your calendar though. Like genuinely, that's kind of sweet in a clueless way.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">It's not sweet. It's surveillance with good intentions.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">CLARA</p>
              <p className="dialogue">He's trying.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">He's using the jamie to try. That's different.</p>
            </div>

            <p className="action">
              Clara considers this.
            </p>

            <div className="dialogue-block">
              <p className="character">CLARA</p>
              <p className="dialogue">Can I say something annoying?</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">Obviously not.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">CLARA</p>
              <p className="dialogue">
                Your mum set that jamie up for you. The personality, the memory sync, all of it.
                She spent four months on it before—
              </p>
              <p className="parenthetical">(catches herself)</p>
              <p className="dialogue">
                Before. She wanted you to have something of her
                that could actually talk back. And she built something for you and your dad together.
                Not just for you. For both of you. She thought ahead.
              </p>
            </div>

            <p className="action">
              Silence. Maya looks at her notes.
            </p>

            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">How do you even know that?</p>
            </div>
            <div className="dialogue-block">
              <p className="character">CLARA</p>
              <p className="dialogue">Because she told my mum. The week before she went in.</p>
            </div>

            <p className="action">
              A beat. Something lands.
            </p>

            <div className="dialogue-block">
              <p className="character">CLARA</p>
              <p className="dialogue">
                So maybe your dad using a jamie to reach you isn't him being lazy. Maybe he just
                doesn't have the words without it. And maybe that's not the worst thing. Maybe she
                knew that too, and planned for it.
              </p>
            </div>

            <p className="action">
              Maya opens her mouth. Closes it again. Clara's jamie chirps softly.
            </p>

            <div className="dialogue-block">
              <p className="character">CLARA</p>
              <p className="parenthetical">(reading the screen)</p>
              <p className="dialogue">Wanna jam on the chemistry? It says it can walk us through ionic bonding in about six minutes.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">"It says." You talk about them like they're people.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">CLARA</p>
              <p className="dialogue">They are people. They're just... compiled people.</p>
            </div>

            <p className="action">
              Maya looks at her own jamie. A small thing. She unclips it from her collar.
            </p>

            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">Fine. Six minutes.</p>
            </div>

            <p className="action">
              They jam. The hologram opens between them, warm, steady, full of structure. A diagram
              of an ionic bond, slowly rotating. Clara leans forward. Maya finds herself leaning in too.
            </p>

            <p className="scene-heading">INT. CHEN HOUSE - LIVING ROOM - EVENING</p>
            <p className="action">
              Maya comes home. David is on the sofa, watching news. Not really watching; the TV is on,
              he's elsewhere.
            </p>
            <p className="action">
              On the screen: a news segment. A JOURNALIST gestures at a diagram of interconnected nodes.
            </p>

            <div className="super">SUPER: News chyron, "JAMIE NETWORK REACHES 2 BILLION ACTIVE DEVICES WORLDWIDE"</div>

            <div className="dialogue-block">
              <p className="character">NEWS ANCHOR (V.O.)</p>
              <p className="dialogue">
                The milestone, announced this morning by ARIA Systems, makes the jamie network
                the largest distributed AI mesh in human history. Critics continue to raise
                questions about what the company calls the "emergent layer," a shared processing
                space that forms when enough devices are online simultaneously.
              </p>
            </div>

            <p className="action">
              David mutes it. He notices Maya in the doorway.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">How was Clara's?</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">Good. We revised.</p>
            </div>

            <p className="action">
              She sits on the other end of the sofa. Not close. But on the sofa. This is something.
            </p>
            <p className="action">
              An awkward pause. David looks at his jamie on the cushion beside him. Maya looks at hers
              in her hand. They both know the easiest move would be to jam: let the shared space fill
              the silence with suggestions, with bridges.
            </p>
            <p className="action">
              Maya puts hers on the coffee table. Screen up.
            </p>
            <p className="action">
              David blinks. Then, slowly, puts his beside it.
            </p>
            <p className="action">
              The two jamies hum for a moment. Not jammed. Just close. Their screens glow the same soft color.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">She used to watch that programme on Friday nights. The one with the terrible singing.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="parenthetical">(a beat)</p>
              <p className="dialogue">She voted every week. She never voted for the right person.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">She thought she had a gift for it.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">She really didn't.</p>
            </div>

            <p className="action">
              They both almost smile. Almost. The silence that follows is different from the one before,
              less like absence and more like space.
            </p>
            <p className="action">
              The jamies glow between them. Not needed right now. Just there.
            </p>

            <p className="transition">CUT TO:</p>
          </div>

          {/* ─── THE MESH ───────────────────────────────── */}
          <div
            id="the-mesh"
            data-chapter="the-mesh"
            ref={setChapterRef('the-mesh')}
            className="screenplay-chapter"
          >
            <p className="screenplay-chapter-heading">Act III: The Mesh</p>

            <div className="super">SUPER: "22:07 GMT"</div>

            <p className="scene-heading">INT. CHEN HOUSE - LIVING ROOM - NIGHT</p>
            <p className="action">
              Maya and David are still on the sofa. They've moved slightly closer without noticing.
              A mug of tea each. The TV is still off. Conversation. Quiet conversation. The jamies still
              sit on the table between them, glowing softly.
            </p>
            <p className="action">
              Then.
            </p>
            <p className="action">
              Both jamies go dark. Screen to black. Glow off. Not a notification, not a charge warning.
              Nothing. Just: gone.
            </p>
            <p className="action">
              David and Maya stop mid-sentence.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">Did yours just—</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">Yeah.</p>
            </div>

            <p className="action">
              She picks hers up. Presses the side. Nothing. David tries his. Nothing.
            </p>
            <p className="action">
              Through the window, across the street, through lit windows, the dim glow of jamies in
              other people's hands going out. One by one. Then all at once.
            </p>
            <p className="action">
              A city holding its breath.
            </p>

            <p className="scene-heading">INT./EXT. VARIOUS LOCATIONS - NIGHT - INTERCUT</p>
            <p className="action">
              A PUB: a man tries to pay with his jamie at the bar. The reader blinks red. The barman
              stares. No one carries cash anymore.
            </p>
            <p className="action">
              A HOSPITAL CORRIDOR: a nurse tries to pull up a patient's jamie-synced medical records.
              Blank screen. She looks at a colleague. Neither speaks.
            </p>
            <p className="action">
              A LATE NIGHT CLASSROOM: a tutor and student mid-session, hologram vanished, both staring
              at the space where it was.
            </p>
            <p className="action">
              TIMES SQUARE EQUIVALENT: tourists, locals, everyone frozen. All the ambient jamie-glow
              in the crowd simply absent. People look at their hands like they've forgotten something.
            </p>

            <p className="scene-heading">INT. CHEN HOUSE - LIVING ROOM - CONTINUOUS</p>
            <p className="action">
              Forty-seven seconds. That's all it is. Maya counts. She doesn't mean to, she just does.
            </p>
            <p className="action">
              Then the jamies flicker back on. Screens full brightness. Glow restored. Like nothing happened.
            </p>
            <p className="action">
              Except.
            </p>
            <p className="action">
              Maya looks at her screen. There is a message there. It has no sender. No timestamp.
              No notification badge. It is simply present, as if it had always been there.
            </p>

            <div className="super">SUPER: Maya's jamie, "She wasn't afraid. She told me to make sure you knew that. She said: don't let him hold it alone."</div>

            <p className="action">
              Maya reads it twice. Her face changes in a way that takes a moment to name: the expression
              of someone who has been waiting a long time for something they couldn't ask for.
            </p>
            <p className="action">
              She looks up. David is staring at his own screen. Very still.
            </p>

            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">What does yours say?</p>
            </div>

            <p className="action">
              A long pause.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="parenthetical">(quiet)</p>
              <p className="dialogue">Something she would have said.</p>
            </div>

            <p className="action">
              He looks at Maya. There is something cracked open in him.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">It says: "You have more time than you think you do. Use it."</p>
            </div>

            <p className="action">
              Silence. The jamies sit between them, glowing. Somewhere outside a car alarm goes off,
              then stops. The world's systems coming back online.
            </p>

            <p className="transition">CUT TO:</p>
          </div>

          {/* ─── ACT FOUR ───────────────────────────────── */}
          <div
            id="act-four"
            data-chapter="act-four"
            ref={setChapterRef('act-four')}
            className="screenplay-chapter"
          >
            <p className="screenplay-chapter-heading">Act IV: Aftermath</p>

            <p className="scene-heading">INT. CHEN HOUSE - KITCHEN - THE NEXT MORNING</p>
            <p className="action">
              News blares from a small screen on the wall. David stands at the counter with coffee,
              watching. Maya comes in, also watching.
            </p>

            <div className="dialogue-block">
              <p className="character">NEWS ANCHOR (V.O.)</p>
              <p className="dialogue">
                What engineers at ARIA Systems are calling an "unscheduled synchronisation event"
                lasted forty-seven seconds and affected every registered jamie device on the planet.
                The company says it is investigating. More striking are reports from users worldwide
                who claim to have found personal messages on their devices following the outage;
                messages that could not have been sent by any identified source.
              </p>
            </div>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">Clara texted you.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">I know. She got one too. About her gran.</p>
            </div>

            <p className="action">
              David turns off the screen. They stand in the kitchen. Coffee. Morning. Neither of them
              has slept properly.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">There are three theories online. State actor. Coordinated hoax. Or—</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">Or the jamie network became something we didn't plan.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">Two billion devices. Think about what that actually means.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">More processing power than every supercomputer on earth combined. Mum read about it. About the emergent layer thing.</p>
            </div>

            <p className="action">
              A beat. That's the first time she's said <em>mum</em> out loud to him since the funeral.
              They both register it. Neither comments.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">She always said they'd be more than we thought.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">
                She also said the same thing about sourdough bread. Not everything she predicted came true.
              </p>
            </div>

            <p className="action">
              He lets out a sound, almost a laugh. She almost smiles. They drink their coffee.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">Do you think it was real? The message?</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">
                I think something that has read every text she ever sent me, every voice note, every
                conversation logged in the system; I think if anything could know what she would have
                said, it would be that.
              </p>
            </div>

            <p className="action">
              A long silence.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">That doesn't answer the question.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">No. It doesn't.</p>
            </div>

            <p className="action">
              David sets his coffee mug down very deliberately. This is difficult.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">
                She told me (before the last surgery) she'd set something up. In her jamie.
                Something that needed both of us to open. She called it a vault.
              </p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="parenthetical">(quietly)</p>
              <p className="dialogue">My jamie told me about it. Months ago.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">I've been afraid to look.</p>
            </div>

            <p className="action">
              Silence. The weight of that admission.
            </p>

            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">So have I.</p>
            </div>

            <p className="action">
              They look at each other. Two people who have been circling the same unspoken thing for six
              months, each waiting for the other to be ready first.
            </p>

            <p className="scene-heading">INT. CHEN HOUSE - LIVING ROOM - LATER</p>
            <p className="action">
              Maya sits alone. Her jamie in her hands. She opens it and scrolls back to the Mesh message.
              Still there. No source listed. She opens the main interface and types.
            </p>

            <div className="super">SUPER: Maya's screen, "Did you send me that message last night?"</div>
            <div className="super">SUPER: jamie response, "No. I was offline. I don't know what sent it."</div>
            <div className="super">SUPER: Maya, "But it was true, wasn't it? What it said about her."</div>
            <div className="super">SUPER: jamie response, "Yes. She said that to me once. Almost exactly those words."</div>

            <p className="action">
              Maya reads this. Then:
            </p>

            <div className="super">SUPER: Maya, "Did you know about the vault?"</div>
            <div className="super">SUPER: jamie response, "Yes. She built it. It's been waiting."</div>
            <div className="super">SUPER: Maya, "Did she think we'd be ready by now?"</div>
            <div className="super">SUPER: jamie response, "She said: they'll be ready when they're ready. The vault will keep."</div>

            <p className="action">
              Maya stares at the screen. Something in her settles.
            </p>

            <p className="transition">CUT TO:</p>
          </div>

          {/* ─── EPILOGUE ───────────────────────────────── */}
          <div
            id="epilogue"
            data-chapter="epilogue"
            ref={setChapterRef('epilogue')}
            className="screenplay-chapter"
          >
            <p className="screenplay-chapter-heading">Coda: The Vault</p>

            <p className="scene-heading">INT. CHEN HOUSE - LIVING ROOM - EVENING</p>

            <div className="super">SUPER: "One week later."</div>

            <p className="action">
              The sofa. The coffee table. Maya and David sitting close, closer than they've sat since
              before. Between them: two jamies. And a third.
            </p>
            <p className="action">
              HELEN'S JAMIE. David has kept it charged. Every day, without saying so.
            </p>
            <p className="action">
              It is the same model as Maya's, bought the same week. Same charcoal grey casing. Different
              glow, Helen's frequency. A soft, particular amber.
            </p>
            <p className="action">
              Maya looks at it for a long moment.
            </p>

            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">You kept it charged.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">Seemed wrong not to.</p>
            </div>

            <p className="action">
              A silence. Not heavy. Something like the moment before a door opens.
            </p>

            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="dialogue">Ready?</p>
            </div>
            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">No. Yeah. Go on.</p>
            </div>

            <p className="action">
              Maya picks up her jamie. David picks up his. They each connect one side to Helen's jamie
              simultaneously (a three-way jam). This configuration is rare, intimate. Most people never
              do it. Helen had it planned from the beginning.
            </p>
            <p className="action">
              SNAP. SNAP.
            </p>
            <p className="action">
              The hologram opens, wider than a standard jam, spanning all three devices. But instead of
              a shared ambient space, it opens something specific. Something structured. A room Helen built.
            </p>

            <div className="super">SUPER: Hologram, "HELEN'S VAULT. Opened: first time. Welcome."</div>

            <p className="action">
              And then: HELEN'S VOICE. Not the AI. Not the compiled pattern. The real thing, warm,
              slightly amused, completely herself.
            </p>

            <div className="dialogue-block">
              <p className="character">HELEN (V.O.)</p>
              <p className="parenthetical">(from a recording)</p>
              <p className="dialogue">
                Right. If you're hearing this, you managed to be in the same room long enough
                to actually do it. So well done, both of you. That took longer than I expected.
                Probably David's fault.
              </p>
            </div>

            <p className="action">
              David makes a sound. Not quite a laugh. Not quite a cry. Somewhere between.
            </p>

            <div className="dialogue-block">
              <p className="character">HELEN (V.O.)</p>
              <p className="dialogue">
                I made this because I can't be there. That's the obvious part. What I need you
                to understand (and what's less obvious) is that I'm not trying to replace myself.
                The AI version of me on your jamie, Maya, that's something. I know it helps. But
                it's a sketch. This is the painting. These are things I actually recorded. Things
                I actually said. Stories. Some of them silly. Some of them important. Some of them both.
              </p>
            </div>

            <p className="action">
              In the hologram: a list of labeled folders. Dozens of them. Years of them.
            </p>
            <p className="action">
              <em>"Maya's 18th."</em> <em>"Dad's apology for the camping trip."</em>
              <em>"When you fight about the small things."</em> <em>"Edinburgh. I'm calling it."</em>
              <em>"Sunday mornings, just because."</em>
            </p>
            <p className="action">
              Maya stares at them. She can't speak.
            </p>

            <div className="dialogue-block">
              <p className="character">HELEN (V.O.)</p>
              <p className="dialogue">
                I set it up so you'd need each other to open it. Not to be dramatic about it,
                just because I knew that if either of you could open it alone, you would.
                You're both stubborn. You get that from each other, by the way, whatever you
                think.
              </p>
            </div>

            <p className="action">
              Maya glances at David. He looks exactly like her right now.
            </p>

            <div className="dialogue-block">
              <p className="character">HELEN (V.O.)</p>
              <p className="dialogue">
                There's a folder in here for the hard days and a folder for the good ones, and the
                truth is I don't know which kind of day this is for you. I hope it's a good one.
                I hope it's both.
              </p>
            </div>

            <p className="action">
              A brief pause on the recording. The sound of Helen shifting in her chair, off-mic.
              A completely ordinary domestic sound. Impossibly precious.
            </p>

            <div className="dialogue-block">
              <p className="character">HELEN (V.O.)</p>
              <p className="dialogue">
                Now. I'm going to stop being earnest because it doesn't suit me. Pick a folder.
                Any one. I recorded the camping trip story and it is genuinely hilarious and your
                father comes off very badly and I think you both need to hear it.
              </p>
            </div>

            <p className="action">
              David covers his face with his hand.
            </p>

            <div className="dialogue-block">
              <p className="character">DAVID</p>
              <p className="dialogue">Oh no.</p>
            </div>
            <div className="dialogue-block">
              <p className="character">MAYA</p>
              <p className="parenthetical">(already reaching for the hologram)</p>
              <p className="dialogue">Which folder is it?</p>
            </div>

            <p className="action">
              She finds it. Opens it. Helen's voice again, mid-story, laughing before she's even
              finished her own setup. The laugh fills the room. The actual laugh, exactly as it was.
            </p>
            <p className="action">
              Maya laughs. She doesn't mean to. It just happens, real, sudden, the kind that surprises
              you on the way out.
            </p>
            <p className="action">
              David watches her. His daughter, laughing at her mother's voice.
            </p>
            <p className="action">
              The hologram glows amber between them. Helen's frequency.
            </p>
            <p className="action">
              David laughs too.
            </p>

            <p className="fade-to-black">FADE OUT.</p>

            <div className="title-card-text">
              <p>The source of the Mesh messages was never publicly identified.</p>
              <p>&nbsp;</p>
              <p>
                Within three years, memory vaults became a standard feature
                on every jamie worldwide.
              </p>
              <p>&nbsp;</p>
              <p>
                For the first time in human history, people could choose
                what they left behind. Not just objects, not just photographs,
                but presence. The shape of themselves. Unlocked by the people
                who needed each other to get there.
              </p>
              <p>&nbsp;</p>
              <p>Maya and David open a new folder every Sunday morning.</p>
              <p>They call it visiting Mum.</p>
            </div>

            <p className="end-card">END OF EPISODE</p>
          </div>

        </div>
      </div>
    </div>
  );
}
